import React from 'react'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'

import { CuppingSession } from '../../types/cuppingSession'
import {
  averageArray,
  averageArrayString,
  calculateSampleScore,
  calculateSampleTotalScore,
  getCuppingSampleCoffeeName,
  getCuppingSampleLabel,
  getEvaluatorScoresForSample,
} from '../../utils/cuppingUtils'
import { CuppingFieldType } from '../../types/enums/cuppingFieldType'
import { CuppingDescriptor, CuppingFormField } from '../../types/cuppingFormConfig'
import TooltipIconButton from '../fields/tooltipIconButton'
import { EvaluatorSampleScore } from '../../types/evaluatorSampleScore'
import Slider from './slider'
import CuppingSampleLink from './cuppingSampleLink'
import ProfileLink from '../logs/profileLink'
import InventoryLink from '../logs/inventoryLink'
import { CuppingSessionStatus } from '../../types/enums/cuppingSessionStatus'
import { hexToRGBA } from '../../utils/graphUtils'

interface ResultsProps {
  session: CuppingSession
  showAverageSlider?: boolean
}

const Results = (props: ResultsProps): JSX.Element => {
  const { t } = useTranslation()
  const theme = useTheme()

  const [expandedSampleIds, setExpandedSampleIds] = React.useState<Array<number | undefined>>([])
  const [expandedNotesIds, setExpandedNotesIds] = React.useState<Array<number | undefined>>([])
  const [expectedValue, setExpectedValue] = React.useState<number | null>(null)

  const isNameColumnVisible = !props.session.is_blind || props.session.status === CuppingSessionStatus.Done
  const summaryFields = props.session.cupping_form_data.form_fields

  const getRoastLevelResult = (roastLevelField: CuppingFormField, scores: Array<number>): number => {
    const colorValues = roastLevelField.field_values.map((x) => x.value)
    // calculate average score, find closest roast level
    const indexLevels = scores.map((x) => colorValues.indexOf(x))
    const averageRoastLevel = indexLevels.reduce((aggr, current) => (aggr += current), 0) / scores.length
    const closestRoastLevelIndex = Array.from(Array(colorValues.length).keys()).reduce(
      (prev, curr) => (Math.abs(curr - averageRoastLevel) < Math.abs(prev - averageRoastLevel) ? curr : prev),
      0
    )
    return closestRoastLevelIndex
  }

  const generateRoastLevelResult = (roastLevelField: CuppingFormField, scores: Array<number>): JSX.Element => {
    if (scores.length === 0) {
      return <>N/A</>
    }
    const colorValues = roastLevelField.field_values.map((x) => x.value)
    const closestRoastLevelIndex = getRoastLevelResult(roastLevelField, scores)
    const roastLevelLabel = roastLevelField.field_values[closestRoastLevelIndex].label

    return (
      <Tooltip title={roastLevelLabel}>
        <Stack direction="row" sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {[...colorValues].reverse().map((colorValue) => (
            <Stack
              key={colorValue}
              sx={{
                backgroundColor: `#${colorValue.toString(16)}`,
                border: `1px solid`,
                borderColor: 'background.default',
                width: colorValue === colorValues[closestRoastLevelIndex] ? 25 : 8,
                height: colorValue === colorValues[closestRoastLevelIndex] ? 50 : 30,
              }}
            />
          ))}
        </Stack>
      </Tooltip>
    )
  }

  const getCheckboxText = (field: CuppingFormField, fieldValue: number) => {
    const min = field.field_values[0].value
    const max = field.field_values[1].value
    const middle = (max + min) / 2
    if (min + max === fieldValue || !fieldValue) {
      return '-'
    }
    if (fieldValue < middle) {
      return field.field_values[0].label
    }
    return field.field_values[1].label
  }

  const generateColorCell = (field: CuppingFormField, fieldValue: number): JSX.Element => {
    const cellColor = valueToColorCell(field, fieldValue)
    const contrast = Math.round(
      cellColor[0] * 0.299 + cellColor[1] * 0.587 + cellColor[2] * 0.114 + (1 - cellColor[3]) * 255
    )
    const textColor = contrast > 150 ? 'text.primary' : 'background.default'
    const colorStyle = props.showAverageSlider
      ? {
          backgroundColor: `rgba(${cellColor[0]},${cellColor[1]},${cellColor[2]},${cellColor[3]})`,
          color: theme.palette.mode === 'dark' ? 'text.primary' : textColor,
        }
      : {}
    return (
      <Typography
        sx={{
          ...colorStyle,
          width: '50px',
          height: '50px',
          lineHeight: '50px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        {field.field_type === CuppingFieldType.Checkbox
          ? getCheckboxText(field, fieldValue)
          : averageArrayString([fieldValue])}
      </Typography>
    )
  }

  const createAverageSlider = (): JSX.Element => {
    // find the common field_type in this cupping form:
    const fieldTypeCount: {
      [key: string]: { formField: CuppingFormField; fieldCount: number }
    } = {}
    // create object with field occurence count
    props.session.cupping_form_data.form_fields.forEach((x) => {
      const oldValue = fieldTypeCount[x.field_type_name]?.fieldCount || 0
      fieldTypeCount[x.field_type_name] = {
        formField: x,
        fieldCount: oldValue + 1,
      }
    })

    // get the field which occurs most times
    const max = Object.keys(fieldTypeCount).reduce((prev, current) => {
      return fieldTypeCount[prev]?.fieldCount > fieldTypeCount[current]?.fieldCount ? prev : current
    })

    if (max) {
      const sliderValue =
        expectedValue !== null
          ? expectedValue
          : fieldTypeCount[max].formField.default_value || getFieldAverageValue(fieldTypeCount[max].formField)

      return (
        <Slider
          value={sliderValue}
          label={t('cupping.expectedvalue')}
          options={fieldTypeCount[max].formField.field_values || []}
          onChange={setExpectedValue}
        />
      )
    }
    return <></>
  }

  const getFieldAverageValue = (field: CuppingFormField): number => {
    const fieldValueMax = Math.max(...field.field_values.map((x) => x.value))
    const fieldValueMin = Math.min(...field.field_values.map((x) => x.value))
    return (fieldValueMax + fieldValueMin) / 2
  }

  const valueToColorCell = (field: CuppingFormField, fieldValue: number): Array<number> => {
    const fieldMinColor = [139, 0, 0]
    const fieldMaxColor = [0, 100, 0]
    const fieldValueMax = Math.max(...field.field_values.map((x) => x.value))
    const fieldValueMin = Math.min(...field.field_values.map((x) => x.value))
    const fieldAvgValue =
      expectedValue !== null && field.field_type !== CuppingFieldType.Checkbox
        ? expectedValue
        : getFieldAverageValue(field)
    const distanceFromAvg = Math.abs(fieldAvgValue - fieldValue)
    // if this is a penalty field (all values 0 or below, treat all values except 0 as negative)
    if (field.field_values.every((x) => x.value <= 0)) {
      const distanceFromMin = fieldValue / fieldValueMin
      return fieldValue < fieldValueMax
        ? [...fieldMinColor, Math.ceil(distanceFromMin * 10) / 10]
        : [...fieldMaxColor, 0.1]
    }
    if (fieldValue < fieldAvgValue) {
      const maxDistanceFromAvg = Math.abs(fieldAvgValue - fieldValueMin)
      const colorPercent1 = distanceFromAvg / maxDistanceFromAvg
      return [...fieldMinColor, colorPercent1]
    } else if (fieldValue >= fieldAvgValue) {
      const maxDistanceFromAvg = Math.abs(fieldAvgValue - fieldValueMax)
      const colorPercent1 = distanceFromAvg / maxDistanceFromAvg
      return [...fieldMaxColor, colorPercent1 || 0.1]
    }
    return [0, 0, 0, 0]
  }

  const getDescriptorsForSample = (
    sampleId: number | undefined
  ): Array<{ descriptor: CuppingDescriptor; evaluators: Array<string> }> => {
    const resultDescriptors: { [id: number]: { descriptor: CuppingDescriptor; evaluators: Array<string> } } = {}
    getEvaluatorScoresForSample(props.session, sampleId).forEach((x) => {
      x.descriptors.forEach((y) => {
        resultDescriptors[y.id] = {
          descriptor: y,
          evaluators: [...(resultDescriptors[y.id]?.evaluators || []), x.evaluator],
        }
      })
    })
    return Object.values(resultDescriptors).sort((a, b) => b.evaluators.length - a.evaluators.length)
  }

  const getFieldScore = (z: EvaluatorSampleScore, field: CuppingFormField): number => {
    return z.scores.find((w) => w.cupping_form_field === field.id)?.score || 0
  }

  const exportScoresAsCsv = (): void => {
    const NEWLINE = '\n'
    const SEPARATOR = ','
    const DESCRIPTOR_SEPARATOR = ';'

    const headers = summaryFields.map((field) => field.field_label)
    let csvContent =
      [
        t('cupping.sample'),
        t('cupping.beanname'),
        t('cupping.profile'),
        t('cupping.batchandevaluator'),
        ...headers,
        t('cupping.totalscore'),
        t('cupping.descriptors'),
        t('cupping.notes'),
      ].join(SEPARATOR) + NEWLINE
    props.session.cupping_samples.forEach((sample, index) => {
      const fieldScores = summaryFields.map((field: CuppingFormField) => {
        const fieldTotalScores = getEvaluatorScoresForSample(props.session, sample.id).map((z: EvaluatorSampleScore) =>
          getFieldScore(z, field)
        )
        if (field.field_type === CuppingFieldType.RoastLevel) {
          return field.field_values[getRoastLevelResult(field, fieldTotalScores)].label
        } else if (field.field_type === CuppingFieldType.Checkbox) {
          return getCheckboxText(field, averageArray(fieldTotalScores))
        } else {
          return averageArrayString(fieldTotalScores)
        }
      })
      csvContent +=
        [
          getCuppingSampleLabel(index),
          sample.log_data?.inventory_name || sample.log_data?.bean_name || '',
          sample.log_data?.profile_name || sample.log_data?.profile_data?.name || '',
          getCuppingSampleCoffeeName(sample, t('logview.batch')),
          fieldScores,
          calculateSampleTotalScore(props.session, sample.id),
          getDescriptorsForSample(sample.id)
            .map((descriptor) => {
              const countFragment = descriptor.evaluators.length > 1 ? `(${descriptor.evaluators.length})` : ''
              return `${descriptor.descriptor.name}${countFragment}`
            })
            .join(DESCRIPTOR_SEPARATOR),
          '',
        ].join(SEPARATOR) + NEWLINE

      getEvaluatorScoresForSample(props.session, sample.id).forEach((z) => {
        const fieldScores = summaryFields.map((sf) => {
          const score = getFieldScore(z, sf)
          if (sf.field_type === CuppingFieldType.RoastLevel) {
            return sf.field_values[getRoastLevelResult(sf, [score])].label
          } else if (sf.field_type === CuppingFieldType.Checkbox) {
            return getCheckboxText(sf, score)
          } else {
            return score
          }
        })

        csvContent +=
          [
            '',
            '',
            '',
            z.evaluator,
            ...fieldScores,
            calculateSampleScore(props.session, z.scores),
            z.descriptors.map((descriptor) => descriptor.name).join(DESCRIPTOR_SEPARATOR),
            z.notes,
          ].join(SEPARATOR) + NEWLINE
      })
    })

    const a = document.createElement('a')
    a.download = `${props.session.name} scores.csv`
    a.href = 'data:text/csv;charset=utf-8,\uFEFF' + encodeURIComponent(csvContent)
    a.target = '_blank'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  return (
    <Stack alignSelf="stretch" flex={1}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h4">{`${props.session.name} ${t('cupping.scores')}`}</Typography>
      </Stack>
      <TableContainer>
        <Table className="cupping-score-table">
          <TableHead>
            <TableRow>
              <TableCell>{t('cupping.sample')}</TableCell>
              {isNameColumnVisible && (
                <>
                  <TableCell>{t('cupping.beanname')}</TableCell>
                  <TableCell>{t('cupping.profile')}</TableCell>
                </>
              )}
              <TableCell>{t('cupping.batchandevaluator')}</TableCell>
              {summaryFields.map((field) => (
                <TableCell sx={{ textAlign: 'center' }} key={field.id}>
                  {field.field_label}
                </TableCell>
              ))}
              {props.session.cupping_form_data.enable_descriptors && <TableCell>{t('cupping.descriptors')}</TableCell>}
              {!!props.session.cupping_form_data.score_calculation && <TableCell>{t('cupping.totalscore')}</TableCell>}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {props.session.cupping_samples.map((sample, index) => {
              return (
                <React.Fragment key={sample.id}>
                  <TableRow sx={{ borderTop: '1px solid', borderColor: 'secondary.light' }}>
                    <TableCell>{getCuppingSampleLabel(index)}</TableCell>
                    {isNameColumnVisible && (
                      <>
                        <TableCell>
                          <InventoryLink log={sample.log_data} />
                        </TableCell>
                        <TableCell>
                          <ProfileLink machineSlug={sample.log_data?.machine_slug || ''} log={sample.log_data} />
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      {isNameColumnVisible ? <CuppingSampleLink sample={sample} /> : t('cupping.hiddenlogname')}
                    </TableCell>
                    {summaryFields.map((field: CuppingFormField) => {
                      const fieldTotalScores = getEvaluatorScoresForSample(props.session, sample.id).map(
                        (z: EvaluatorSampleScore) => getFieldScore(z, field)
                      )
                      return (
                        <TableCell key={`${field.id}-${sample.id}`} sx={{ textAlign: 'center' }}>
                          {field.field_type === CuppingFieldType.RoastLevel
                            ? generateRoastLevelResult(field, fieldTotalScores)
                            : generateColorCell(field, averageArray(fieldTotalScores))}
                        </TableCell>
                      )
                    })}
                    {props.session.cupping_form_data.enable_descriptors && (
                      <TableCell>
                        {getDescriptorsForSample(sample.id).map((y) => {
                          const countFragment = y.evaluators.length > 1 ? ` (${y.evaluators.length})` : ''
                          return (
                            <Tooltip key={y.descriptor.id} title={y.evaluators.join(', ')}>
                              <Chip sx={{ mr: 1, mb: 1 }} label={`${y.descriptor.name}${countFragment}`} />
                            </Tooltip>
                          )
                        })}
                      </TableCell>
                    )}
                    {!!props.session.cupping_form_data.score_calculation && (
                      <TableCell>
                        <Typography variant="h4">{calculateSampleTotalScore(props.session, sample.id)}</Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      {getEvaluatorScoresForSample(props.session, sample.id).length > 0 && (
                        <TooltipIconButton
                          onClick={() => {
                            setExpandedSampleIds((oldIds) => {
                              const isExpanded = oldIds.indexOf(sample.id) > -1
                              return isExpanded ? oldIds.filter((id) => id !== sample.id) : [...oldIds, sample.id]
                            })
                          }}
                          icon={
                            expandedSampleIds.indexOf(sample.id) > -1 ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )
                          }
                          tooltip={
                            expandedSampleIds.indexOf(sample.id) > -1
                              ? t('cupping.hideevaluatorscores')
                              : t('cupping.showevaluatorscores')
                          }
                        />
                      )}
                    </TableCell>
                  </TableRow>
                  {expandedSampleIds.indexOf(sample.id) > -1 &&
                    getEvaluatorScoresForSample(props.session, sample.id).map((z: EvaluatorSampleScore) => {
                      return (
                        <React.Fragment key={`${sample.id}-${z.evaluator}`}>
                          <TableRow sx={{ backgroundColor: hexToRGBA(theme.palette.primary.main, 0.1) }}>
                            <TableCell />
                            {isNameColumnVisible && (
                              <>
                                <TableCell />
                                <TableCell />
                              </>
                            )}
                            <TableCell>
                              {z.evaluator}
                              {z.notes && (
                                <TooltipIconButton
                                  onClick={() =>
                                    setExpandedNotesIds((oldIds) => {
                                      const isExpanded = oldIds.indexOf(z.id) > -1
                                      return isExpanded ? oldIds.filter((id) => id !== z.id) : [...oldIds, z.id]
                                    })
                                  }
                                  icon={
                                    expandedNotesIds.indexOf(z.id) > -1 ? (
                                      <KeyboardArrowUpIcon />
                                    ) : (
                                      <KeyboardArrowDownIcon />
                                    )
                                  }
                                  tooltip={
                                    expandedNotesIds.indexOf(z.id) > -1
                                      ? t('cupping.hidenotes')
                                      : t('cupping.shownotes')
                                  }
                                />
                              )}
                            </TableCell>
                            {summaryFields.map((sf) => (
                              <TableCell key={`${sample.id}-${sf.id}`} sx={{ textAlign: 'center' }}>
                                {sf.field_type === CuppingFieldType.RoastLevel
                                  ? generateRoastLevelResult(sf, [getFieldScore(z, sf)])
                                  : generateColorCell(sf, getFieldScore(z, sf))}
                              </TableCell>
                            ))}
                            {props.session.cupping_form_data.enable_descriptors && (
                              <TableCell>
                                {z.descriptors.map((descriptor) => {
                                  return (
                                    <Chip
                                      key={`${z.evaluator}-${descriptor.id}`}
                                      sx={{ mr: 1, mb: 1 }}
                                      label={descriptor.name}
                                    />
                                  )
                                })}
                              </TableCell>
                            )}
                            {!!props.session.cupping_form_data.score_calculation && (
                              <TableCell>{calculateSampleScore(props.session, z.scores)}</TableCell>
                            )}
                            <TableCell />
                          </TableRow>
                          {expandedNotesIds.indexOf(z.id) > -1 && (
                            <TableRow>
                              <TableCell colSpan={isNameColumnVisible ? 3 : 1} />
                              <TableCell colSpan={100}>{z.notes}</TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      )
                    })}
                </React.Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack sx={{ maxWidth: 300, flex: 1 }}>{props.showAverageSlider && createAverageSlider()}</Stack>
        {props.session.session_scores.length > 0 && (
          <Button onClick={exportScoresAsCsv}>{t('cupping.exportscoresascsv')}</Button>
        )}
      </Stack>
    </Stack>
  )
}

export default Results
